import { ContentDownload } from '@fs/zion-icon'
import { usePageView } from '@fs/zion-analytics'
import { Button, H5 } from '@fs/zion-ui'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageTitle } from '../app/PageTitleContext'
import usePatron from '../../hooks/artifacts/usePatron'
import usePatronArtifacts from '../../hooks/artifacts/usePatronArtifacts'
import { ARTIFACT_CATEGORY } from './util/artifactsConstants'
import { downloadArtifactBatch } from './util/downloadUtils'

// Choose a batch size that is small enough for the browser and JSZip to hold all the artifact blobs in memory
const BATCH_SIZE = 500

const STATUS = {
  WAITING: 0, // Waiting for user to initiate download
  IN_PROGRESS: 1, // Downloading batch(es)
  FINISHED: 2, // All batches finished
}

/**
 * A page component that allows an admin to download all of a patron’s artifacts,
 * batched into .zip files. Downloading and compressing artifacts is done on the frontend.
 * Expects a userQuery in the route params, e.g. mp.12345 or un.someusername
 *
 * Future enhancement: move most of the downloadUtils functions into a web worker, to
 * avoid generating the zip on the main thread. Importing jszip/axios/other modules from a
 * web worker requires a custom webpack config and isn’t feasible with Aug 2019 frontier stack.
 */
export default function DownloadPatronArtifacts({
  match: {
    params: { userQuery, deleted = false },
  },
}) {
  usePageView({ page_detail: 'DMConsole:DownloadPatronArtifacts' })
  const [t] = useTranslation()
  usePageTitle(t('pageTitle.artifacts.downloadPatron'))
  const [status, setStatus] = useState(STATUS.WAITING)
  const [currentArtifact, setCurrentArtifact] = useState(0)
  const [currentBatch, setCurrentBatch] = useState(1)
  const [paginatorOffset, setPaginatorOffset] = useState(0)
  const [fetchedArtifactIds, setFetchedArtifactIds] = useState([])
  const patron = usePatron(userQuery, false)

  // Get current batch of artifact objects
  const {
    artifacts,
    pageCount: batchCount,
    totalArtifactsSpanningPages,
  } = usePatronArtifacts({
    userQuery,
    artifactCategory: ARTIFACT_CATEGORY.ANY,
    paginatorOffset,
    paginatorCount: BATCH_SIZE,
    includeAssociatedArtifacts: true,
    artifactStatus: deleted === 'true' ? 'tombstoned' : '',
    patron: patron.patron,
  })

  const startDownload = () => {
    setStatus(STATUS.IN_PROGRESS)
  }

  const onArtifactComplete = () => {
    // Update the progress
    setCurrentArtifact((current) => current + 1)
  }

  const onBatchComplete = useCallback(() => {
    // Fetch the next batch, or mark as finished
    if (currentBatch < batchCount) {
      // Update the progress and trigger the hook fetching the next batch
      setTimeout(() => {
        setCurrentArtifact(0)
        setCurrentBatch((current) => current + 1)
        setPaginatorOffset(paginatorOffset + BATCH_SIZE)
      }, 100)
    } else {
      setStatus(STATUS.FINISHED)
    }
  }, [batchCount, currentBatch, paginatorOffset])

  // When the currentBatch state changes, start downloading that batch
  useEffect(() => {
    if (status !== STATUS.IN_PROGRESS) {
      return
    }
    // Ensure the batch hasn’t already been fetched
    if (fetchedArtifactIds.includes(artifacts[0].id)) {
      return
    }
    setFetchedArtifactIds((previous) => [...previous, ...artifacts.map((a) => a.id)])

    const zipName = `patron_memories${batchCount > 1 ? `_${currentBatch}` : ''}`
    downloadArtifactBatch(artifacts, zipName, onArtifactComplete, onBatchComplete)
  }, [status, fetchedArtifactIds, artifacts, currentBatch, batchCount, onBatchComplete])

  if (!artifacts) {
    return null
  }
  if (artifacts.length === 0) {
    return <div>{t('artifacts.noArtifactsFound', 'No artifacts found')}</div>
  }

  return (
    <>
      <H5 supplementary>{`${t('artifacts.download.downloadPatronArtifacts', 'Download Artifacts Uploaded by')} ${
        patron.patron.name
      }`}</H5>
      {status === STATUS.WAITING && (
        <>
          <H5 supplementary>{`${totalArtifactsSpanningPages === 0 ? artifacts.length : totalArtifactsSpanningPages} ${t(
            'artifacts.download.artifactBatchCount',
            'artifacts will be downloaded as a zip file.'
          )}`}</H5>
          <br />
          <Button Icon={ContentDownload} onClick={startDownload} emphasis="high">
            {t('artifacts.download.startDownload', 'Start Download')}
          </Button>
        </>
      )}
      {status === STATUS.IN_PROGRESS && (
        <div style={{ textAlign: 'center' }}>
          <p>
            {currentArtifact < artifacts.length
              ? t('artifacts.counter', {
                  defaultValue: `Artifact ${currentArtifact} of ${artifacts.length}`,
                  currentArtifact,
                  total: artifacts.length,
                })
              : t('artifacts.download.zippingBatch', 'Zipping batch...')}
            <br />
            {/* The last batch might be smaller than the BATCH_SIZE constant, so display actual size */}
            <progress value={currentArtifact} max={artifacts.length} style={{ width: '200px' }} />
          </p>

          {batchCount > 1 && (
            <p>
              Batch {currentBatch} of {batchCount}
              <br />
              <progress value={currentBatch - 1} max={batchCount} style={{ width: '200px' }} />
            </p>
          )}
          <br />
          <Button onClick={() => window.location.reload()}>{t('action.cancel', 'Cancel')}</Button>
        </div>
      )}
      {status === STATUS.FINISHED && <div>{t('artifacts.downloadSuccessful', 'Download successful')}</div>}
      <br />
    </>
  )
}

DownloadPatronArtifacts.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      userQuery: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
}
